// AuthContext (architecture.md ADR-5, §19). Holds the session + live profile. Server data
// (event types, bookings…) lives in TanStack Query, not here.
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/apiClient.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [team, setTeam] = useState(null); // agency context (owner or employee)
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const { user } = await api.me();
      setProfile(user);
      // Agency context is only relevant to employees or team-enabled owners.
      if (user?.role === 'EMPLOYEE' || user?.allow_teams) {
        try {
          setTeam(await api.agencyContext());
        } catch {
          setTeam(null);
        }
      } else {
        setTeam(null);
      }
      return user; // callers (login) route by role off this
    } catch {
      setProfile(null);
      setTeam(null);
      return null;
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) await loadProfile();
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      if (s) await loadProfile();
      else setProfile(null);
    });
    return () => sub.subscription.unsubscribe();
  }, [loadProfile]);

  // Everyone (owner, employee, super-admin) signs in here with email + password. Returns the
  // loaded profile so the caller can redirect by role (employees → their scoped home).
  const login = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    return loadProfile();
  }, [loadProfile]);

  const signup = useCallback(async ({ name, username, email, password, turnstile_token: turnstileToken }) => {
    // Create the account via the API (creates an UNCONFIRMED auth user + profile and emails a
    // 6-digit code). We do NOT sign in here — the email must be verified first. The caller
    // shows the code-entry screen and then calls verifySignup.
    // turnstile_token is consumed by the API's bot-check middleware.
    return api.signup({ name, username, email, password, turnstile_token: turnstileToken });
  }, []);

  // Verify the 6-digit signup code (confirms the account), then sign in. Returns the loaded
  // profile so the caller can route by role.
  const verifySignup = useCallback(async (email, code, password) => {
    await api.verifyOtp(email, code);
    return login(email, password);
  }, [login]);

  const resendSignupOtp = useCallback((email) => api.resendOtp(email), []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = {
    session,
    profile,
    team,
    loading,
    isAuthenticated: !!session,
    isSuperAdmin: profile?.role === 'SUPER_ADMIN',
    isEmployee: profile?.role === 'EMPLOYEE',
    canManageTeam: profile?.allow_teams === true && profile?.role !== 'EMPLOYEE',
    mustResetPassword: team?.role === 'employee' && team?.membership?.must_reset_password === true,
    login,
    signup,
    verifySignup,
    resendSignupOtp,
    logout,
    reloadProfile: loadProfile,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
